import { useEffect, useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { aboutContent } from '@/mocks/faqs';
import { siteInfo } from '@/mocks/domains';

const FORM_URL = 'https://readdy.ai/api/form/d9qs0fme3oq4jqumtpsg';

export default function About() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = `关于我们 - ${siteInfo.name}`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', `了解${siteInfo.name}的品牌故事、发展历程与口碑评价。国内领先的独立资讯爆料平台，致力于为公众提供真实深入的资讯内容。`);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get('phone_alt');
    if (honeypot && typeof honeypot === 'string' && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          params.append(key, value as string);
        }
      });

      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: any = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || responseText || '提交失败';
        if (msg.includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请稍后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteInfo.name,
    url: 'https://heiliaowang.com',
    description: siteInfo.description,
    email: aboutContent.contact.email,
    telephone: aboutContent.contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20 pb-14">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', to: '/' },
                { label: '关于我们' },
              ]}
            />

            {/* Page Header */}
            <div className="mt-6 mb-12 text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-primary-100">
                <i className="ri-flashlight-fill text-3xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-3xl text-foreground-900 mb-2">关于黑料网</h1>
              <p className="text-sm text-foreground-500 max-w-lg mx-auto leading-relaxed">
                国内领先的独立资讯爆料平台，坚持真实、深度、独立的报道原则
              </p>
            </div>

            {/* Brand Introduction */}
            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-building-2-line text-primary-500"></i>
                品牌介绍
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                {aboutContent.brandIntro.split('\n\n').filter(Boolean).map((paragraph, i) => (
                  <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-4 last:mb-0">
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>

            {/* Reputation */}
            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-medal-line text-accent-500"></i>
                口碑与荣誉
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  {[
                    { icon: 'ri-trophy-line', value: '最具影响力', label: '独立资讯平台' },
                    { icon: 'ri-user-star-line', value: '5000万+', label: '月活跃用户' },
                    { icon: 'ri-star-line', value: '4.8/5.0', label: '读者满意度评分' },
                    { icon: 'ri-award-line', value: '多次获奖', label: '行业新闻奖项' },
                  ].map((item) => (
                    <div key={item.label} className="flex items-center gap-4 p-4 rounded-lg bg-background-100">
                      <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-100 flex-shrink-0">
                        <i className={`${item.icon} text-xl text-accent-600`}></i>
                      </div>
                      <div>
                        <div className="font-heading text-lg text-foreground-900">{item.value}</div>
                        <div className="text-xs text-foreground-500">{item.label}</div>
                      </div>
                    </div>
                  ))}
                </div>
                {aboutContent.reputation.split('•').filter(Boolean).map((item, i) => (
                  <p key={i} className="text-sm text-foreground-700 leading-relaxed mb-2 last:mb-0">
                    • {item.trim()}
                  </p>
                ))}
              </div>
            </section>

            {/* Contact Form */}
            <section className="mb-14">
              <h2 className="font-heading text-xl text-foreground-900 mb-4 flex items-center gap-2">
                <i className="ri-mail-send-line text-primary-500"></i>
                联系我们
              </h2>
              <div className="bg-background-50 border border-background-200/70 rounded-lg p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <h3 className="font-heading text-base text-foreground-900 mb-4">联系方式</h3>
                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-mail-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{aboutContent.contact.email}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-phone-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{aboutContent.contact.phone}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-wechat-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{aboutContent.contact.wechat}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-map-pin-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{aboutContent.contact.address}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 flex-shrink-0">
                          <i className="ri-time-line text-primary-600"></i>
                        </div>
                        <span className="text-sm text-foreground-700">{aboutContent.contact.workingHours}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-heading text-base text-foreground-900 mb-4">发送消息</h3>
                    <form onSubmit={handleSubmit} data-readdy-form="" className="space-y-3">
                      <div>
                        <input
                          type="text"
                          name="name"
                          placeholder="您的姓名"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <input
                          type="email"
                          name="email"
                          placeholder="您的邮箱"
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
                        />
                      </div>
                      <div>
                        <textarea
                          name="message"
                          placeholder="请输入您的留言内容（最多500字）"
                          maxLength={500}
                          rows={4}
                          required
                          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-200 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                        ></textarea>
                      </div>
                      {/* Honeypot */}
                      <div className="visually-hidden-field">
                        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                      </div>
                      <button
                        type="submit"
                        className="w-full py-2.5 rounded-lg bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发送消息
                      </button>

                      {formStatus === 'success' && (
                        <p className="text-sm text-emerald-600 text-center">消息已发送，我们会尽快回复您！</p>
                      )}
                      {formStatus === 'error' && (
                        <p className="text-sm text-accent-600 text-center">{formError}</p>
                      )}
                    </form>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}